
/* ============================== src/hooks/useChatStats.js ============================== */
// src/hooks/useChatStats.js
import { useMemo } from "react";
import { useUnreadCountsQuery } from "../api/queries/notifications";
import { useChatReadsQuery } from "../api/queries/chat-reads";
import { useCommentsQuery } from "../api/queries/comments";
import { chatKeysFor } from "../utils/chatKey";

/**
 * Непрочитанные: число берётся с сервера (GET /chat-reads/unread),
 * который сам считает max(keys).
 */
export function useChatUnreadCount(userId, projectId, taskId = null) {
  const { data: counts } = useUnreadCountsQuery();

  return useMemo(() => {
    if (!counts) return 0;
    if (taskId) return counts[`task:${taskId}`] || 0;
    if (projectId) return counts[`project:${projectId}`] || 0;
    return 0;
  }, [counts, projectId, taskId]);
}

/**
 * Индекс непрочитанных по всем чатам: Map<chatKey, count>.
 */
export function useUnreadCommentIndex() {
  const { data: counts } = useUnreadCountsQuery();

  return useMemo(() => {
    const map = new Map();
    if (!counts) return map;
    for (const [key, count] of Object.entries(counts)) {
      map.set(key, count);
    }
    return map;
  }, [counts]);
}

/**
 * Lookup отметок «прочитано до» — для подсветки отдельного сообщения.
 * Сервер отдаёт сырые отметки, max(keys) вычисляем здесь только для
 * рендера «это сообщение — новое?».
 */
export function useChatReadLookup(userId) {
  const { data: reads } = useChatReadsQuery();

  return useMemo(() => {
    const lastReadByKey = new Map();
    for (const r of reads ?? []) {
      if (r.userId === userId) lastReadByKey.set(r.chatKey, r.lastReadAt);
    }
    return {
      getLastReadAt: (key) => lastReadByKey.get(key) || 0,
      isUnread: (comment) => {
        if (!comment || comment.authorId === userId) return false;
        let max = 0;
        for (const key of chatKeysFor(comment)) {
          const v = lastReadByKey.get(key) || 0;
          if (v > max) max = v;
        }
        return comment.createdAt > max;
      },
    };
  }, [reads, userId]);
}

/**
 * Общее число сообщений в чате. Считается из загруженного списка.
 * Возвращает undefined, пока данные грузятся, чтобы Tabs не рисовал "(0)".
 */
export function useChatTotalCount(projectId, taskId = null) {
  const { data: comments } = useCommentsQuery(
    { projectId, taskId },
    { enabled: !!projectId || !!taskId },
  );

  if (comments === undefined) return undefined;
  return comments.length;
}
